// Report calculation utilities

import type { G50Data } from '@/types/report';

// TVA calculation (19% in Algeria)
export const TVA_RATE = 0.19;
export const FISCAL_STAMP = 100; // 100 DZD fiscal stamp

export function calculateTVA(amountHT: number): number {
  return Math.round(amountHT * TVA_RATE * 100) / 100;
}

export function calculateTTC(amountHT: number): number {
  return amountHT + calculateTVA(amountHT);
}

export function calculateHT(amountTTC: number): number {
  return Math.round((amountTTC / (1 + TVA_RATE)) * 100) / 100;
}

export function extractTVA(amountTTC: number): number {
  const ht = calculateHT(amountTTC);
  return amountTTC - ht;
}

// Growth rate calculation
export function calculateGrowthRate(current: number, previous: number): number {
  if (previous === 0) return current > 0 ? 100 : 0;
  return Math.round(((current - previous) / previous) * 100 * 100) / 100;
}

// Average calculation
export function calculateAverage(total: number, count: number): number {
  if (count === 0) return 0;
  return Math.round((total / count) * 100) / 100;
}

// Percentage calculation
export function calculatePercentage(part: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((part / total) * 100 * 100) / 100;
}

// G50 calculations (Algerian TVA declaration)
export function calculateG50(
  salesHT: number,
  purchasesHT: number,
  companyInfo: {
    name: string;
    nif: string;
    address: string;
    wilaya: string;
  },
  month: number,
  year: number
): G50Data {
  const tvaCollectee = calculateTVA(salesHT);
  const tvaDeductible = calculateTVA(purchasesHT);
  const netTVA = tvaCollectee - tvaDeductible;
  
  return {
    period: { month, year },
    company: companyInfo,
    salesHT,
    tvaCollectee,
    salesTTC: salesHT + tvaCollectee,
    purchasesHT,
    tvaDeductible,
    purchasesTTC: purchasesHT + tvaDeductible,
    tvaToPay: netTVA > 0 ? netTVA : 0,
    tvaCredit: netTVA < 0 ? Math.abs(netTVA) : 0,
    netTVA,
  };
}

// Number formatting
export function formatCurrency(amount: number, showCurrency = true): string {
  const formatted = new Intl.NumberFormat('fr-DZ', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
  
  return showCurrency ? `${formatted} د.ج` : formatted;
}

export function formatNumber(num: number): string {
  return new Intl.NumberFormat('fr-DZ').format(num);
}

export function formatPercentage(value: number): string {
  return `${value.toFixed(2)}%`;
}

// Stock calculations
export function calculateStockValue(quantity: number, unitPrice: number): number {
  return Math.round(quantity * unitPrice * 100) / 100;
}

export function calculateTurnoverRate(
  soldQuantity: number,
  avgStock: number,
  days: number
): number {
  if (avgStock === 0) return 0;
  const turnoverPerDay = soldQuantity / days;
  const daysOfStock = avgStock / (turnoverPerDay || 1);
  return Math.round((365 / daysOfStock) * 100) / 100;
}

// Client aging calculations
export function calculatePaymentDelay(invoiceDate: Date, paymentDate: Date | null): number {
  if (!paymentDate) {
    // If not paid, calculate days since invoice
    const today = new Date();
    const diffTime = today.getTime() - invoiceDate.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }
  
  const diffTime = paymentDate.getTime() - invoiceDate.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

export function categorizeAging(daysPast: number): '0-30' | '31-60' | '61-90' | '90+' {
  if (daysPast <= 30) return '0-30';
  if (daysPast <= 60) return '31-60';
  if (daysPast <= 90) return '61-90';
  return '90+';
}

// Forecast calculations (simple linear projection)
export function forecastCashFlow(
  historicalData: Array<{ date: Date; amount: number }>,
  daysToForecast: number
): number {
  if (historicalData.length < 2) return 0;
  
  // Calculate average daily change
  const sortedData = [...historicalData].sort((a, b) => a.date.getTime() - b.date.getTime());
  const firstAmount = sortedData[0].amount;
  const lastAmount = sortedData[sortedData.length - 1].amount;
  
  const daysDiff = (sortedData[sortedData.length - 1].date.getTime() - sortedData[0].date.getTime()) / (1000 * 60 * 60 * 24);
  
  if (daysDiff === 0) return lastAmount;
  
  const dailyChange = (lastAmount - firstAmount) / daysDiff;
  const forecastAmount = lastAmount + (dailyChange * daysToForecast);
  
  return Math.round(forecastAmount * 100) / 100;
}

// Summary statistics
export interface SummaryStats {
  sum: number;
  avg: number;
  min: number;
  max: number;
  count: number;
}

export function calculateSummaryStats(values: number[]): SummaryStats {
  if (values.length === 0) {
    return { sum: 0, avg: 0, min: 0, max: 0, count: 0 };
  }
  
  const sum = values.reduce((acc, val) => acc + val, 0);
  const avg = sum / values.length;
  const min = Math.min(...values);
  const max = Math.max(...values);
  
  return {
    sum: Math.round(sum * 100) / 100,
    avg: Math.round(avg * 100) / 100,
    min: Math.round(min * 100) / 100,
    max: Math.round(max * 100) / 100,
    count: values.length,
  };
}
